import React, { useEffect, useMemo, useState } from 'react';
import {
  Search,
  Globe,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  User,
  Server,
  Star,
  HelpCircle,
  CreditCard,
  Shield,
  Zap,
  Users,
  Info,
  Target,
  Settings,
  Lock,
  BookOpen,
  ArrowRight
} from 'lucide-react';
import * as Icons from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import { API_URLS } from '../../config/api';
import MediaDisplay from '../MediaDisplay';
import { safeParseFloat } from '../../utils/format';

const resolveIcon = (iconName, fallback = 'CheckCircle') => {
  if (!iconName && Icons[fallback]) return Icons[fallback];
  if (iconName && Icons[iconName]) return Icons[iconName];
  if (fallback && Icons[fallback]) return Icons[fallback];
  return CheckCircle;
};

const DomainPage = () => {
  const [domainInput, setDomainInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedDomains, setSelectedDomains] = useState([]);
  const [contentReady, setContentReady] = useState(false);
  const [domainPrices, setDomainPrices] = useState([]);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [pageContent, setPageContent] = useState(null);
  const [domainBlogs, setDomainBlogs] = useState([]);
  const [domainFaqs, setDomainFaqs] = useState([]);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  const fetchContent = async () => {
    try {
      const response = await fetch(API_URLS.PAGE_CONTENT('alan-adi'));
      const json = await response.json();
      if (json.status === 'success' && json.data) {
        const rawAdditional = json.data.additional_sections;
        let additional = {};
        if (typeof rawAdditional === 'string' && rawAdditional.trim().startsWith('{')) {
          try {
            additional = JSON.parse(rawAdditional);
          } catch (error) {
            console.error('Domain additional_sections parse error:', error);
            additional = {};
          }
        } else if (rawAdditional && typeof rawAdditional === 'object') {
          additional = rawAdditional;
        }
        setPageContent({
          ...json.data,
          additional_sections: additional
        });
      } else {
        setPageContent(null);
      }
    } catch (error) {
      console.error('Domain sayfa içeriği alınamadı:', error);
      setPageContent(null);
    }
  };

  useEffect(() => {
    fetchContent();
    
    // Fetch domain blog posts
    fetch(API_URLS.BLOGS)
      .then((res) => res.json())
      .then((data) => {
        if (data.status === 'success' && data.blogs) {
          // Filter domain/alan-adi category blogs, limit to 3
          const domainCategory = data.blogs.filter(
            (blog) => (blog.category === 'domain' || blog.category === 'alan-adi') && blog.is_published === 1
          ).slice(0, 3);
          setDomainBlogs(domainCategory);
        }
      })
      .catch((err) => console.error('Error fetching domain blogs:', err));

    // Fetch domain FAQs
    fetch(API_URLS.FAQ)
      .then((res) => res.json())
      .then((data) => {
        if (data.categories && data.questions) {
          // Find "Alan Adı" category by title
          const alanAdiCategory = data.categories.find(
            (cat) => cat.title?.toLowerCase().includes('alan ad') || 
                     cat.slug === 'alan-adi' || cat.slug === 'alan adi' || cat.slug === 'domain'
          );
          
          if (alanAdiCategory) {
            // Filter questions for this category
            const categoryQuestions = data.questions.filter(
              (q) => q.category_id === alanAdiCategory.id
            );
            setDomainFaqs(categoryQuestions);
          }
        } else {
          console.log('Invalid FAQ response format:', data);
        }
      })
      .catch((err) => console.error('Error fetching domain FAQs:', err));
  }, []);

  // Domain fiyatlarını API'den çek
  useEffect(() => {
    const fetchDomainPrices = async () => {
      try {
        setPricesLoading(true);
        const response = await fetch(API_URLS.DOMAIN_PRICES);
        const data = await response.json();
        
        if (data.status === 'success' && Array.isArray(data.data)) {
          // Format prices for display
          const formatted = data.data.map(item => {
            const currency = item.currency === 'USD' ? '$' : item.currency === 'TRY' ? '₺' : '€';
            return {
              tld: item.tld,
              price: `${currency}${safeParseFloat(item.registration_price).toFixed(2)}`,
              originalPrice: item.original_price ? `${currency}${safeParseFloat(item.original_price).toFixed(2)}` : null,
              isPromo: !!item.is_promo,
              renewal: `${currency}${safeParseFloat(item.renewal_price).toFixed(2)}`,
              transfer: `${currency}${safeParseFloat(item.transfer_price).toFixed(2)}`
            };
          });
          setDomainPrices(formatted);
        } else {
          // Fallback to static data if API fails
          setDomainPrices([
            { tld: '.com', price: '$11.65', originalPrice: '$11.86', isPromo: true, renewal: '$11.86', transfer: '$10.93' },
            { tld: '.net', price: '$14.41', originalPrice: '$14.43', isPromo: true, renewal: '$14.43', transfer: '$14.43' },
            { tld: '.org', price: '$8.23', originalPrice: '$14.43', isPromo: true, renewal: '$14.43', transfer: '$14.43' },
            { tld: '.co', price: '$30.89', originalPrice: '$32.96', isPromo: true, renewal: '$32.96', transfer: '$30.90' },
            { tld: '.site', price: '$1.02', originalPrice: '$32.20', isPromo: true, renewal: '$32.20', transfer: '$32.20' },
            { tld: '.biz', price: '$22.67', originalPrice: null, isPromo: false, renewal: '$22.67', transfer: '$22.67' },
            { tld: '.info', price: '$4.11', originalPrice: '$24.73', isPromo: true, renewal: '$24.73', transfer: '$24.73' },
            { tld: '.online', price: '$0.99', originalPrice: '$41.77', isPromo: true, renewal: '$41.77', transfer: '$41.77' },
          ]);
        }
      } catch (error) {
        console.error('Domain fiyatları yüklenemedi:', error);
        // Fallback to static data
        setDomainPrices([
          { tld: '.com', price: '$11.65', originalPrice: '$11.86', isPromo: true, renewal: '$11.86', transfer: '$10.93' },
          { tld: '.net', price: '$14.41', originalPrice: '$14.43', isPromo: true, renewal: '$14.43', transfer: '$14.43' },
          { tld: '.org', price: '$8.23', originalPrice: '$14.43', isPromo: true, renewal: '$14.43', transfer: '$14.43' },
          { tld: '.co', price: '$30.89', originalPrice: '$32.96', isPromo: true, renewal: '$32.96', transfer: '$30.90' },
          { tld: '.site', price: '$1.02', originalPrice: '$32.20', isPromo: true, renewal: '$32.20', transfer: '$32.20' },
          { tld: '.biz', price: '$22.67', originalPrice: null, isPromo: false, renewal: '$22.67', transfer: '$22.67' },
          { tld: '.info', price: '$4.11', originalPrice: '$24.73', isPromo: true, renewal: '$24.73', transfer: '$24.73' },
          { tld: '.online', price: '$0.99', originalPrice: '$41.77', isPromo: true, renewal: '$41.77', transfer: '$41.77' },
        ]);
      } finally {
        setPricesLoading(false);
      }
    };

    fetchDomainPrices();
  }, []);

  const derivedContent = useMemo(() => {
    const additionalRaw = pageContent?.additional_sections;
    const additional = additionalRaw && typeof additionalRaw === 'object' ? additionalRaw : {};
    const heroAdditional = additional.hero && typeof additional.hero === 'object' ? additional.hero : {};
    const featureSection =
      additional.feature_cards && typeof additional.feature_cards === 'object' ? additional.feature_cards : {};
    const whatIsSection =
      additional.what_is_section && typeof additional.what_is_section === 'object' ? additional.what_is_section : {};
    const purposeSection =
      additional.purpose_section && typeof additional.purpose_section === 'object' ? additional.purpose_section : {};
    const purchaseSection =
      additional.purchase_section && typeof additional.purchase_section === 'object' ? additional.purchase_section : {};
    const pricingSection =
      additional.pricing_section && typeof additional.pricing_section === 'object' ? additional.pricing_section : {};
    const faqSection = additional.faq_section && typeof additional.faq_section === 'object' ? additional.faq_section : {};
    const professionalMgmt = additional.professional_management && typeof additional.professional_management === 'object' ? additional.professional_management : {};
    const ownerCapabilities = additional.domain_owner_capabilities && typeof additional.domain_owner_capabilities === 'object' ? additional.domain_owner_capabilities : {};
    const selectionTips = additional.domain_selection_tips && typeof additional.domain_selection_tips === 'object' ? additional.domain_selection_tips : {};

    return {
      hero: {
        badge: heroAdditional.badge || pageContent?.hero_badge_text || 'Domain & Hosting',
        title: heroAdditional.title || pageContent?.hero_title || 'Domain Sorgulama',
        description: heroAdditional.description || pageContent?.hero_description || '0,99$\'dan Başlayan Fiyatlarla Harika Bir Domain Kaydedin!',
        highlight_text: heroAdditional.highlight_text || '',
        image_url: heroAdditional.image_url || pageContent?.hero_image_url || '',
        bullets: Array.isArray(heroAdditional.bullets) && heroAdditional.bullets.length > 0 
          ? heroAdditional.bullets 
          : [
              'Anında domain kayıt ve aktivasyon',
              'Ücretsiz WHOIS gizleme',
              'Kolay yönetim paneli',
              'Türkçe teknik destek'
            ]
      },
      features: {
        title: featureSection.title || 'Neden Website Al ile Domain?',
        subtitle: featureSection.subtitle || 'Profesyonel domain hizmetleriyle işinizi güvence altına alın',
        cards: Array.isArray(featureSection.cards) && featureSection.cards.length > 0
          ? featureSection.cards
          : [
              { icon: 'Globe', title: 'Geniş Uzantı Seçeneği', description: '500+ domain uzantısı ile size özel adres' },
              { icon: 'Shield', title: 'Güvenli Altyapı', description: 'SSL sertifikası ve güvenlik önlemleri' },
              { icon: 'Zap', title: 'Hızlı Aktivasyon', description: 'Anında domain kaydı ve DNS yönetimi' },
              { icon: 'Users', title: '7/24 Destek', description: 'Türkçe teknik destek ekibi her zaman yanınızda' }
            ]
      },
      what_is_section: {
        title: whatIsSection.title || 'Domain Nedir?',
        description: whatIsSection.description || 'Domain (alan adı), internet üzerindeki adresinizdir. Web sitenizin kolay hatırlanabilir ve erişilebilir olmasını sağlar. example.com gibi benzersiz bir isimle markanızı internette temsil edersiniz.',
        highlight_card:
          whatIsSection.highlight_card && typeof whatIsSection.highlight_card === 'object' && Object.keys(whatIsSection.highlight_card).length > 0
            ? whatIsSection.highlight_card
            : {
                icon: 'Info',
                title: 'Önemli Not',
                description: 'Domain adınız işletmenizin dijital kimliğidir. Doğru seçim yapmak için uzman ekibimizden destek alabilirsiniz.'
              }
      },
      purpose_section: {
        title: purposeSection.title || 'Domain Ne İşe Yarar?',
        description: purposeSection.description || 'Domain adı, web sitenizi internette tanımlayan benzersiz adrestir. Müşterilerinizin sizi kolayca bulmasını sağlar, marka kimliğinizi güçlendirir ve profesyonel bir imaj oluşturur.',
        highlight_card:
          purposeSection.highlight_card && typeof purposeSection.highlight_card === 'object' && Object.keys(purposeSection.highlight_card).length > 0
            ? purposeSection.highlight_card
            : {
                icon: 'Target',
                title: 'Marka Kimliği',
                description: 'Domain adınız işletmenizin ilk izlenimidir. Kısa, akılda kalıcı ve profesyonel bir domain seçin.'
              }
      },
      purchase_section: {
        title: purchaseSection.title || 'Domain Nasıl Alınır?',
        description: purchaseSection.description || 'Domain satın alma işlemi oldukça basittir. İstediğiniz domain adını sorgulayın, uygunsa sepete ekleyin ve güvenli ödeme ile hemen kullanmaya başlayın. Tüm süreç 5 dakika içinde tamamlanır.',
        highlight_card:
          purchaseSection.highlight_card && typeof purchaseSection.highlight_card === 'object' && Object.keys(purchaseSection.highlight_card).length > 0
            ? purchaseSection.highlight_card
            : {
                icon: 'CheckCircle',
                title: 'Hızlı Süreç',
                description: 'Domain sorgulama, kayıt ve aktivasyon işlemi sadece 5 dakika. Anında kullanmaya başlayın!'
              }
      },
      pricing_section: {
        title: pricingSection.title || 'Domain Fiyatları',
        description: pricingSection.description || 'En popüler domain uzantıları için özel fiyatlarımızı inceleyin. Yıllık kayıt, yenileme ve transfer ücretleri şeffaf olarak listelenmiştir.'
      },
      faq_section: {
        title: faqSection.title || 'Sıkça Sorulan Sorular',
        subtitle: faqSection.subtitle || 'Domain hizmetleri hakkında merak ettikleriniz',
        items: Array.isArray(faqSection.items) && faqSection.items.length > 0
          ? faqSection.items
          : []
      },
      professional_management: {
        title: professionalMgmt.title || 'Profesyonel Domain Yönetimi',
        subtitle: professionalMgmt.subtitle || 'Alan Adınızı Profesyonel Yönetim Özellikleri ve Ayrıcalıklı Servisler ile Yönetin!',
        features: Array.isArray(professionalMgmt.features) && professionalMgmt.features.length > 0
          ? professionalMgmt.features
          : []
      },
      owner_capabilities: {
        title: ownerCapabilities.title || 'Domain Sahibi Olarak Yapabilecekleriniz',
        subtitle: ownerCapabilities.subtitle || 'Bir Domain Kaydederek Sahip Olacağınız Bazı Ayrıcalıklar!',
        image_url: ownerCapabilities.image_url || '',
        cards: Array.isArray(ownerCapabilities.cards) && ownerCapabilities.cards.length > 0
          ? ownerCapabilities.cards
          : []
      },
      selection_tips: {
        title: selectionTips.title || 'Domain Seçerken Dikkat Etmeniz Gerekenler',
        subtitle: selectionTips.subtitle || 'İyi Bir Alan Adı Belirlemek İçin Nelere Dikkat Etmelisiniz!',
        tips: Array.isArray(selectionTips.tips) && selectionTips.tips.length > 0
          ? selectionTips.tips
          : []
      }
    };
  }, [pageContent]);

  const splitParagraphs = (text) =>
    typeof text === 'string'
      ? text
          .split(/\n+/)
          .map((paragraph) => paragraph.trim())
          .filter(Boolean)
      : [];

  const normalizeImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    return `${window.location.origin}${imageUrl.startsWith('/') ? '' : '/'}${imageUrl}`;
  };

  const heroData = derivedContent.hero;
  const heroBullets = Array.isArray(heroData.bullets) ? heroData.bullets : [];
  const featureContent = derivedContent.features;
  const featureCards = Array.isArray(featureContent.cards) ? featureContent.cards : [];
  const pricingContent = derivedContent.pricing_section;
  const faqContent = derivedContent.faq_section;
  const infoSectionsConfig = [];

  // Use database FAQs if available, otherwise fallback to page content
  const displayFaqs = domainFaqs.length > 0 ? domainFaqs : (faqContent.items || []);

  // Blog articles for domain category
  const domainArticles = domainBlogs.length > 0 ? domainBlogs.map(blog => ({
    title: blog.title,
    excerpt: blog.excerpt,
    link: `/blog/${blog.slug}`,
    tag: blog.level || 'Domain',
    image: normalizeImageUrl(blog.image)
  })) : [];

  const heroBadge = heroData.badge;
  const heroTitleText = heroData.title;
  const heroDescriptionText = heroData.description;
  const heroHighlight = heroData.highlight_text;

  const handleDomainSearch = async () => {
    if (!domainInput.trim()) return;

    // Alan adını temizle ve normalize et
    let cleanDomain = domainInput.trim().toLowerCase();
    
    // Eğer domain uzantısı yoksa veya geçersizse, sadece domain adını al
    // Yoksa tüm domain'i kullan
    const domainParts = cleanDomain.split('.');
    if (domainParts.length === 1) {
      // Uzantı yok, domain adını olduğu gibi kullan
      cleanDomain = domainParts[0];
    }
    
    // Harici domain sorgulama sayfasını yeni sekmede aç
    const externalUrl = `https://my.website-al.com/alan-adi?domain=${encodeURIComponent(cleanDomain)}`;
    window.open(externalUrl, '_blank');
  };

  const handleDomainSelect = (domain) => {
    if (selectedDomains.find(d => d.domain === domain.domain)) {
      setSelectedDomains(selectedDomains.filter(d => d.domain !== domain.domain));
    } else {
      setSelectedDomains([...selectedDomains, domain]);
    }
  };

  const handleSelectAll = () => {
    const availableDomains = searchResults.filter(d => d.status === 'available' && !d.premium);
    if (selectedDomains.length === availableDomains.length) {
      setSelectedDomains([]);
    } else {
      setSelectedDomains(availableDomains);
    }
  };

  const getStatusIcon = (status, premium) => {
    if (premium) return <Star className="w-5 h-5 text-yellow-500" />;
    if (status === 'available') return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (status === 'unavailable') return <XCircle className="w-5 h-5 text-red-500" />;
    return <Clock className="w-5 h-5 text-gray-500" />;
  };

  const getStatusText = (status, premium) => {
    if (premium) return 'Premium';
    if (status === 'available') return 'Müsait';
    if (status === 'unavailable') return 'Müsait Değil';
    return 'Bilinmiyor';
  };

  const getStatusBadge = (status, premium) => {
    if (premium) return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">Premium</Badge>;
    if (status === 'available') return <Badge variant="default" className="bg-green-100 text-green-800">Müsait</Badge>;
    if (status === 'unavailable') return <Badge variant="destructive">Müsait Değil</Badge>;
    return <Badge variant="outline">Bilinmiyor</Badge>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Hero Section */}
      <section
        className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white"
        style={{
          opacity: contentReady ? 1 : 0,
          transition: 'opacity 0.6s ease-in'
        }}
      >
        <div className="container mx-auto px-4">
          {/* Domain Sorgulama Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
              {derivedContent.hero.title || 'Domain Sorgulama'}
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl text-yellow-300 font-semibold">
              {derivedContent.hero.description || '0,99$\'dan Başlayan Fiyatlarla Harika Bir Domain Kaydedin!'}
            </p>
          </div>

          {/* Domain Search */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-2xl p-8 shadow-2xl">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Globe className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Alan adınızı yazın (örn: example.com)"
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    className="pl-12 h-14 text-lg text-black border-2 border-gray-200 focus:border-blue-500 placeholder:!text-black"
                    onKeyPress={(e) => e.key === 'Enter' && handleDomainSearch()}
                  />
                </div>
                <Button
                  onClick={handleDomainSearch}
                  disabled={isSearching || !domainInput.trim()}
                  className="h-14 px-8 text-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  {isSearching ? (
                    <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Search className="w-5 h-5 mr-2" />
                  )}
                  {isSearching ? 'Aranıyor...' : 'Sorgula'}
                </Button>
              </div>
            </div>
          </div>

          {/* Domain Prices */}
          <div className="mt-12">
            {pricesLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <RefreshCw className="inline-block w-12 h-12 text-white mb-4 animate-spin" />
                  <p className="text-white text-lg">Fiyatlar yükleniyor...</p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                {domainPrices.slice(0, 8).map((domain, index) => (
                  <div key={index} className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center">
                    <div className="text-2xl font-bold mb-2">{domain.tld}</div>
                    <div className="space-y-1">
                      {domain.originalPrice && (
                        <div className="text-sm text-gray-300 line-through opacity-75 mb-1">
                          {domain.originalPrice}
                        </div>
                      )}
                      <div className="text-lg font-semibold text-yellow-300">
                        {domain.price} /yıllık
                      </div>
                    </div>
                    {domain.isPromo && (
                      <Badge variant="secondary" className="mt-2 bg-yellow-500 text-white">
                        Kampanya
                      </Badge>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Profesyonel Domain Yönetimi */}
      {derivedContent.professional_management.features.length > 0 && (
        <section 
          className="py-20 bg-gradient-to-br from-indigo-900 to-blue-900"
          style={{
            opacity: contentReady ? 1 : 0,
            transition: 'opacity 0.6s ease-in'
          }}
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                {derivedContent.professional_management.title}
              </h2>
              <p className="text-xl text-blue-200">
                {derivedContent.professional_management.subtitle}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {derivedContent.professional_management.features.map((feature, index) => {
                const FeatureIcon = resolveIcon(feature.icon, 'CheckCircle');
                return (
                  <Card key={`prof-mgmt-${index}`} className="bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20 transition-all duration-300 hover:scale-105">
                    <CardContent className="p-8 text-center">
                      <div className="w-20 h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6">
                        <FeatureIcon className="w-10 h-10 text-yellow-300" />
                      </div>
                      <h3 className="text-xl font-bold text-white mb-4">{feature.title}</h3>
                      <p className="text-blue-100">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <section 
          className="py-16"
          style={{
            opacity: contentReady ? 1 : 0,
            transition: 'opacity 0.6s ease-in'
          }}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">Arama Sonuçları</h2>
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedDomains.length === searchResults.filter(d => d.status === 'available' && !d.premium).length}
                        onChange={handleSelectAll}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span className="text-sm text-gray-600">Tümünü Seç</span>
                    </label>
                  </div>
                </div>

                <div className="space-y-4">
                  {searchResults.map((result, index) => (
                    <div
                      key={index}
                      className={`border rounded-lg p-6 transition-all duration-200 ${
                        result.status === 'available' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'
                      }`}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-center">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(result.status, result.premium)}
                          <div>
                            <div className="font-semibold text-lg">{result.domain}</div>
                            <div className="text-sm text-gray-600">{result.tld} uzantısı</div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          {getStatusBadge(result.status, result.premium)}
                        </div>

                        <div className="text-center">
                          <div className="text-xl font-bold text-gray-800">{result.price}</div>
                          {result.isPromo && (
                            <div className="text-sm text-gray-500 line-through">{result.originalPrice}</div>
                          )}
                        </div>

                        <div className="flex justify-center">
                          {result.status === 'available' && !result.premium && (
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={selectedDomains.find(d => d.domain === result.domain) !== undefined}
                                onChange={() => handleDomainSelect(result)}
                                className="w-5 h-5 text-blue-600"
                              />
                              <span className="text-sm font-medium text-blue-600">Seç</span>
                            </label>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {selectedDomains.length > 0 && (
                  <div className="mt-8 p-6 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-blue-800">
                          {selectedDomains.length} domain seçildi
                        </h3>
                        <p className="text-blue-600">
                          Toplam: {selectedDomains.reduce((sum, d) => sum + safeParseFloat(d.price.replace(/[^\d,]/g, '').replace(',', '.')), 0).toFixed(2)} ₺
                        </p>
                      </div>
                      <Button className="bg-blue-600 hover:bg-blue-700">
                        Sepete Ekle ve Devam Et
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {infoSectionsConfig.map(({ key, data, fallbackIcon, background, reverse }) => {
        const paragraphs = splitParagraphs(data.description);
        const highlight = data.highlight_card || {};
        const hasHighlight = highlight.title || highlight.description;
        const hasContent = data.title || paragraphs.length > 0 || hasHighlight;
        if (!hasContent) {
          return null;
        }
        const HighlightIcon = resolveIcon(highlight.icon, fallbackIcon);
        return (
          <section
            key={key}
            className={`py-16 ${background}`}
            style={{
              opacity: contentReady ? 1 : 0,
              transition: 'opacity 0.6s ease-in'
            }}
          >
            <div className="container mx-auto px-4">
              <div className={`grid grid-cols-1 gap-12 items-center ${hasHighlight ? 'lg:grid-cols-2' : ''}`}>
                <div className={`${reverse && hasHighlight ? 'lg:order-2' : ''} text-center md:text-left`}>
                  {data.title && (
                    <h2 className="text-3xl font-bold text-gray-800 mb-6">
                      {data.title}
                    </h2>
                  )}
                  {paragraphs.length > 0 && (
                    <div className="space-y-4 text-gray-600">
                      {paragraphs.map((paragraph, index) => (
                        <p key={`${key}-paragraph-${index}`}>{paragraph}</p>
                      ))}
                    </div>
                  )}
                </div>
                {hasHighlight && (
                  <div className={`${reverse ? 'lg:order-1' : ''} text-center`}>
                    <div className="bg-white rounded-2xl p-8 shadow-xl">
                      <HighlightIcon className="w-24 h-24 text-blue-600 mx-auto mb-6" />
                      {highlight.title && (
                        <h3 className="text-2xl font-bold text-gray-800 mb-4">{highlight.title}</h3>
                      )}
                      {highlight.description && <p className="text-gray-600">{highlight.description}</p>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      })}

      {/* Domain Fiyatları Section */}
      <section 
        className="py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              {pricingContent.title && (
                <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">{pricingContent.title}</h2>
              )}
              {pricingContent.description && (
                <p className="text-gray-600 text-base md:text-lg max-w-3xl mx-auto px-4">{pricingContent.description}</p>
              )}
            </div>

          <div className="max-w-6xl mx-auto">
            {pricesLoading ? (
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden p-12">
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <RefreshCw className="inline-block w-16 h-16 text-blue-600 mb-4 animate-spin" />
                    <p className="text-gray-600 text-lg">Fiyat listesi yükleniyor...</p>
                  </div>
                </div>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block bg-white rounded-2xl shadow-xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-blue-600 text-white">
                        <tr>
                          <th className="px-6 py-4 text-left font-semibold">
                            <Globe className="w-5 h-5 inline mr-2" />
                            Uzantı
                          </th>
                          <th className="px-6 py-4 text-center font-semibold">
                            <CreditCard className="w-5 h-5 inline mr-2" />
                            Kayıt
                          </th>
                          <th className="px-6 py-4 text-center font-semibold">
                            <RefreshCw className="w-5 h-5 inline mr-2" />
                            Yenileme
                          </th>
                          <th className="px-6 py-4 text-center font-semibold">
                            <RefreshCw className="w-5 h-5 inline mr-2" />
                            Transfer
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {domainPrices.map((domain, index) => (
                          <tr key={index} className={`border-b ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <span className="font-semibold text-lg">{domain.tld}</span>
                                {domain.isPromo && (
                                  <Badge variant="secondary" className="ml-2 bg-yellow-500 text-white">
                                    Kampanya
                                  </Badge>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              {domain.originalPrice ? (
                                <div>
                                  <div className="text-sm text-gray-500 line-through">{domain.originalPrice} /yıllık</div>
                                  <div className="font-bold text-green-600">{domain.price} /yıllık</div>
                                </div>
                              ) : (
                                <div className="font-bold">{domain.price} /yıllık</div>
                              )}
                            </td>
                            <td className="px-6 py-4 text-center font-semibold">
                              {domain.renewal} /yıllık
                            </td>
                            <td className="px-6 py-4 text-center font-semibold">
                              {domain.transfer} /yıllık
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                  {domainPrices.map((domain, index) => (
                    <Card key={index} className="bg-white shadow-lg">
                      <CardContent className="p-4">
                        {/* Header */}
                        <div className="flex items-center justify-between mb-4 pb-3 border-b">
                          <div className="flex items-center space-x-2">
                            <Globe className="w-5 h-5 text-blue-600" />
                            <span className="font-bold text-lg">{domain.tld}</span>
                            {domain.isPromo && (
                              <Badge variant="secondary" className="bg-yellow-500 text-white text-xs">
                                Kampanya
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Prices */}
                        <div className="space-y-3">
                          {/* Kayıt Fiyatı */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 text-gray-600">
                              <CreditCard className="w-4 h-4" />
                              <span className="text-sm font-medium">Kayıt</span>
                            </div>
                            <div className="text-right">
                              {domain.originalPrice ? (
                                <div>
                                  <div className="text-xs text-gray-400 line-through">{domain.originalPrice}</div>
                                  <div className="font-bold text-green-600">{domain.price} /yıllık</div>
                                </div>
                              ) : (
                                <div className="font-bold">{domain.price} /yıllık</div>
                              )}
                            </div>
                          </div>

                          {/* Yenileme Fiyatı */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 text-gray-600">
                              <RefreshCw className="w-4 h-4" />
                              <span className="text-sm font-medium">Yenileme</span>
                            </div>
                            <div className="text-right font-semibold">{domain.renewal} /yıllık</div>
                          </div>

                          {/* Transfer Fiyatı */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 text-gray-600">
                              <RefreshCw className="w-4 h-4" />
                              <span className="text-sm font-medium">Transfer</span>
                            </div>
                            <div className="text-right font-semibold">{domain.transfer} /yıllık</div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Domain Sahibi Olarak Yapabilecekleriniz */}
      {derivedContent.owner_capabilities.cards.length > 0 && (
        <section 
          className="py-20 bg-gray-50"
          style={{
            opacity: contentReady ? 1 : 0,
            transition: 'opacity 0.6s ease-in'
          }}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Sol Taraf - Başlık ve Görsel */}
                <div>
                  {/* Başlık */}
                  <div className="mb-8 text-center md:text-left">
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                      {derivedContent.owner_capabilities.title}
                    </h2>
                    <p className="text-xl text-gray-600">
                      {derivedContent.owner_capabilities.subtitle}
                    </p>
                  </div>

                {/* Görsel - Başlık Altında */}
                <div className="hidden md:flex items-center justify-start">
                  {derivedContent.owner_capabilities.image_url ? (
                    <div className="relative w-full max-w-lg">
                      <MediaDisplay
                        src={normalizeImageUrl(derivedContent.owner_capabilities.image_url)}
                        alt="Domain Sahibi"
                        className="w-full rounded-3xl shadow-2xl object-cover hover:scale-105 transition-transform duration-500"
                        videoProps={{
                          style: { maxHeight: '400px' }
                        }}
                        imgProps={{
                          style: { maxHeight: '400px' }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="relative w-full max-w-lg h-96">
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl transform rotate-3"></div>
                      <div className="relative bg-white rounded-3xl shadow-2xl p-8 transform -rotate-2 hover:rotate-0 transition-transform duration-500 h-full">
                        <div className="grid grid-cols-2 gap-6 h-full">
                          <div className="flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                              <Globe className="w-10 h-10 text-blue-600" />
                            </div>
                            <div className="text-center">
                              <div className="h-3 bg-gray-200 rounded w-24 mb-2 mx-auto"></div>
                              <div className="h-2 bg-gray-100 rounded w-16 mx-auto"></div>
                            </div>
                          </div>
                          <div className="flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-3">
                              <Server className="w-10 h-10 text-green-600" />
                            </div>
                            <div className="text-center">
                              <div className="h-3 bg-gray-200 rounded w-24 mb-2 mx-auto"></div>
                              <div className="h-2 bg-gray-100 rounded w-16 mx-auto"></div>
                            </div>
                          </div>
                          <div className="flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mb-3">
                              <Shield className="w-10 h-10 text-purple-600" />
                            </div>
                            <div className="text-center">
                              <div className="h-3 bg-gray-200 rounded w-24 mb-2 mx-auto"></div>
                              <div className="h-2 bg-gray-100 rounded w-16 mx-auto"></div>
                            </div>
                          </div>
                          <div className="flex flex-col items-center justify-center">
                            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-3">
                              <Zap className="w-10 h-10 text-blue-600" />
                            </div>
                            <div className="text-center">
                              <div className="h-3 bg-gray-200 rounded w-24 mb-2 mx-auto"></div>
                              <div className="h-2 bg-gray-100 rounded w-16 mx-auto"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Sağ Taraf - Kartlar 2x2 Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {derivedContent.owner_capabilities.cards.map((card, index) => (
                  <Card key={`owner-card-${index}`} className="hover:shadow-xl transition-shadow duration-300">
                    <CardContent className="p-6">
                      <h3 className="text-xl font-bold text-gray-800 mb-3 text-center">
                        {card.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 text-center">
                        {card.description}
                      </p>
                      {card.button_text && (
                        <div className="text-center">
                          <Button 
                            className="bg-blue-500 hover:bg-blue-600 px-6 py-2 rounded-full text-sm"
                            onClick={() => {
                              if (card.button_link === '#') {
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                              } else {
                                window.location.href = card.button_link;
                              }
                            }}
                          >
                            {card.button_text}
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* Domain Seçerken Dikkat Etmeniz Gerekenler */}
      {derivedContent.selection_tips.tips.length > 0 && (
        <section 
          className="py-20 bg-white"
          style={{
            opacity: contentReady ? 1 : 0,
            transition: 'opacity 0.6s ease-in'
          }}
        >
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              {/* Başlık */}
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
                  {derivedContent.selection_tips.title}
                </h2>
                <p className="text-2xl text-gray-600">
                  {derivedContent.selection_tips.subtitle}
                </p>
              </div>

              {/* Liste */}
              <div className="space-y-8">
                {derivedContent.selection_tips.tips.map((tip, index) => (
                  <div key={`tip-${index}`} className="flex flex-col md:flex-row gap-6 items-start text-center md:text-left">
                    <div className="flex-shrink-0 mx-auto md:mx-0">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <CheckCircle className="w-7 h-7 text-blue-500" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl md:text-2xl font-bold text-gray-800 mb-3">
                        {tip.title}
                      </h3>
                      <p className="text-base md:text-lg text-gray-600">
                        {tip.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Domain Blog Highlights */}
      {domainArticles.length > 0 && (
        <section
          className="py-16 bg-white"
          style={{ opacity: contentReady ? 1 : 0, transition: 'opacity 0.6s ease-in' }}
        >
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-10">
              <div>
                <Badge className="bg-blue-100 text-blue-700">Blog</Badge>
                <h2 className="text-3xl font-bold text-gray-900 mt-3">Alan Adı ve Domain Hakkında İpuçları</h2>
                <p className="text-gray-600 mt-2 max-w-3xl">
                  Domain seçimi, yönetimi ve güvenlik üzerine yayınladığımız rehberleri keşfedin.
                </p>
              </div>
              <Button
                variant="outline"
                className="border-blue-200 text-blue-600 hover:bg-blue-50"
                onClick={() => window.location.href = '/blog?category=alan-adi'}
              >
                Tüm Domain Yazılarını Gör
              </Button>
            </div>
            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
              {domainArticles.map((article, idx) => {
                const hasValidImage = article.image && 
                  article.image !== 'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=800&q=80' &&
                  !article.image.includes('placeholder');
                
                return (
                  <Card key={idx} className="overflow-hidden border-blue-100 hover:shadow-lg transition-shadow">
                    {hasValidImage ? (
                      <div className="aspect-[16/9] bg-blue-50 overflow-hidden">
                        <MediaDisplay
                          src={article.image}
                          alt={article.title}
                          className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                          imgProps={{
                            loading: 'lazy',
                            onError: (e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center"><svg class="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <div className="aspect-[16/9] bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                        <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                        </svg>
                      </div>
                    )}
                    <CardContent className="p-5 space-y-3">
                      <div className="inline-flex items-center px-3 py-1 bg-blue-50 text-blue-600 text-xs font-semibold rounded-full">
                        <BookOpen className="w-3.5 h-3.5 mr-1" />
                        {article.tag}
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">{article.title}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{article.excerpt}</p>
                      <button
                        onClick={() => window.open(article.link, '_blank')}
                        className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-700"
                      >
                        Yazıyı Oku
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {(faqContent.title || faqContent.subtitle || displayFaqs.length > 0) && (
        <section
          className="py-16 bg-gray-50"
          style={{
            opacity: contentReady ? 1 : 0,
            transition: 'opacity 0.6s ease-in'
          }}
        >
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              {faqContent.title && <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">{faqContent.title}</h2>}
              {faqContent.subtitle && (
                <p className="text-gray-600 text-base md:text-lg max-w-3xl mx-auto px-4">{faqContent.subtitle}</p>
              )}
            </div>

            <div className="max-w-4xl mx-auto">
              {displayFaqs.length > 0 ? (
                <Accordion type="single" collapsible className="w-full">
                  {displayFaqs.map((item, index) => (
                    <AccordionItem key={`faq-item-${index}`} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left">
                        <div className="flex items-center">
                          <HelpCircle className="w-5 h-5 mr-3 text-blue-600" />
                          {item.question}
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600">{item.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <p className="text-center text-gray-500">Henüz SSS içeriği eklenmemiş.</p>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default DomainPage;
